const pricingTiers = require("./data.json");

/**
 * Calculate FBA shipping cost based on dimensions, weight, and product price
 *
 * IMPORTANT: This calculator uses price-based fee tiers:
 * - priceUsd >= $13: Uses fee_gte13 rates (standard pricing)
 * - priceUsd < $13: Uses fee_lt13 rates (approximately $0.91 cheaper)
 *
 * Size categories (based on FBA pricing card):
 * - Parcel: max<=45, mid<=34, min<=20, weight<=12kg
 * - Small Oversize: max<=61, weight<=2kg
 * - Standard Oversize: max<=105, weight<=22kg
 * - Large Oversize: weight<=35kg (catch-all for oversized dims)
 * - Special Oversize: weight>35kg, <250kg
 *
 * @param {number} lengthCm - Length in centimeters
 * @param {number} widthCm - Width in centimeters
 * @param {number} heightCm - Height in centimeters
 * @param {number} weightKg - Actual weight in kilograms
 * @param {number} priceUsd - Product price in USD (default: 13)
 * @returns {number} Shipping fee in USD
 */
const calculateShipping = (lengthCm, widthCm, heightCm, weightKg, priceUsd = 13) => {
  const dims = [lengthCm, widthCm, heightCm].sort((a, b) => b - a);
  const [maxDim, midDim, minDim] = dims;

  const volumetricWeight = (lengthCm * widthCm * heightCm) / 4000;
  const billingWeight = Math.max(weightKg, volumetricWeight);

  const isGte13 = priceUsd >= 13;
  const feeKey = isGte13 ? "fee_gte13" : "fee_lt13";

  let category = null;

  if (maxDim <= 45 && midDim <= 34 && minDim <= 20 && weightKg <= 12) {
    category = pricingTiers[0]; // Parcel
  } else if (maxDim <= 61 && weightKg <= 2) {
    category = pricingTiers[1]; // Small Oversize
  } else if (maxDim <= 105 && weightKg <= 22) {
    category = pricingTiers[2]; // Standard Oversize
  } else if (weightKg <= 35) {
    category = pricingTiers[3]; // Large Oversize
  } else if (weightKg < 250) {
    category = pricingTiers[4]; // Special Oversize
  } else {
    return 99999;
  }

  for (const tier of category.tiers) {
    if (billingWeight <= tier.maxWeight) {
      return tier[feeKey];
    }
  }

  if (category.overflow) {
    const { baseFee_gte13, baseFee_lt13, perKgOver, overThreshold } = category.overflow;
    const baseFee = isGte13 ? baseFee_gte13 : baseFee_lt13;
    const excessWeight = billingWeight - overThreshold;
    return baseFee + excessWeight * perKgOver;
  }

  return 99999;
};

const getCategory = (lengthCm, widthCm, heightCm, weightKg) => {
  const dims = [lengthCm, widthCm, heightCm].sort((a, b) => b - a);
  const [maxDim, midDim, minDim] = dims;

  if (maxDim <= 45 && midDim <= 34 && minDim <= 20 && weightKg <= 12) {
    return "Parcel";
  } else if (maxDim <= 61 && weightKg <= 2) {
    return "Small Oversize";
  } else if (maxDim <= 105 && weightKg <= 22) {
    return "Standard Oversize";
  } else if (weightKg <= 35) {
    return "Large Oversize";
  } else if (weightKg < 250) {
    return "Special Oversize";
  } else {
    return "Overweight - Not Supported";
  }
};

const getVolumetricWeight = (lengthCm, widthCm, heightCm) => {
  return (lengthCm * widthCm * heightCm) / 4000;
};

const getBillingWeight = (lengthCm, widthCm, heightCm, weightKg) => {
  const volumetricWeight = getVolumetricWeight(lengthCm, widthCm, heightCm);
  return Math.max(weightKg, volumetricWeight);
};

module.exports = {
  calculateShipping,
  getCategory,
  getVolumetricWeight,
  getBillingWeight
};
